/**
 * Заказ файла и ожидание сборки — один механизм на весь кабинет.
 *
 * Кнопка называется «Скачать PDF», и она обязана СКАЧАТЬ, а не завести заявку,
 * притворяющуюся кнопкой. За этим стоит цепочка, которая одинакова и в центре
 * отчётов, и на обзоре бренда: найти уже собранный такой же файл → если его нет,
 * заказать сборку → дождаться готовности опросом → увести браузер на файл.
 *
 * **Числа в файле сходятся с экраном не потому, что мы их туда переписали.**
 * Сборщик кладёт в отчёт те самые округлённые числа, которые видел клиент, и
 * при расхождении с пересчитанными отказывается собирать файл вовсе. Поэтому
 * `failed` здесь — сработавшая защита, и её причина написана для человека:
 * такие отказы доходят до экрана словами, а не «ошибкой 500».
 *
 * Второй копии этой цепочки в кабинете нет намеренно: ожидание сборки — это
 * сроки, шаг опроса, поведение при обрыве связи и разбор причины отказа, и
 * разъехавшиеся копии означали бы, что одна кнопка ждёт три минуты, а вторая
 * сдаётся через десять секунд.
 */

import { api, downloadFile, idempotencyKey, requestWith } from './client';
import { ApiError } from './problem';
import type { components } from './schema';

type Report = components['schemas']['Report'];
type ReportKind = components['schemas']['ReportKind'];
type ReportBuildFormat = components['schemas']['ReportBuildFormat'];

/**
 * Сколько ждать сборку заказанного кнопкой файла, прежде чем сказать словами.
 *
 * Воркер отчётов KEDA поднимает с нуля, и первый файл после простоя ждёт
 * дольше остальных. Три минуты — щедро для сборки и честно для человека,
 * который смотрит на кнопку: дольше он всё равно решит, что всё сломалось.
 */
const EXPORT_TIMEOUT_MS = 180_000;
/** Опрос сборки: тот же шаг, что и у списка, если сервер не сказал иначе. */
const EXPORT_POLL_MS = 2_000;
/** Дальше шаг не растим, даже если сервер молчит. */
const EXPORT_POLL_MAX_MS = 16_000;

export const TOO_SLOW =
  'Файл собирается дольше обычного. Закрывать страницу не нужно: отчёт появится в списке ' +
  'сам, и оттуда его можно будет скачать.';
export const BUILD_FAILED =
  'Отчёт собрать не удалось. Соберите его заново — если повторится, напишите нам.';

/**
 * Отказ, у которого есть текст для человека.
 *
 * Отдельный тип нужен, потому что `ErrorNotice` показывает словами только
 * `ApiError`, а всё остальное превращает в «Что-то не сработало на нашей
 * стороне» — и правильно делает: `TypeError` из недр фронта человеку не
 * поможет. Но причина отказа сборки написана сборщиком ДЛЯ ЧЕЛОВЕКА
 * («числа разошлись с экраном»), и терять её нельзя. Такие отказы показываются
 * своим сообщением рядом с кнопкой.
 */
export class ExportFailure extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ExportFailure';
  }
}

/** Пауза, которую можно прервать: уход со страницы не должен ждать её конца. */
function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const abort = () => {
      window.clearTimeout(timer);
      reject(new DOMException('Ожидание отменено', 'AbortError'));
    };
    const timer = window.setTimeout(() => {
      signal.removeEventListener('abort', abort);
      resolve();
    }, ms);
    signal.addEventListener('abort', abort, { once: true });
  });
}

/**
 * Дождаться готового файла заказанного отчёта.
 *
 * Возвращает готовый отчёт; бросает `ExportFailure` на отказе сборки и на
 * исчерпании срока, `AbortError` — если со страницы ушли.
 *
 * ОБРЫВ СВЯЗИ НЕ ЗАКАНЧИВАЕТ ОЖИДАНИЕ. Сборщик в это время работает, и
 * единственный не дошедший запрос — не повод объявить, что файла не будет:
 * шаг опроса растёт, а срок остаётся общим. А вот отказ, который сам себя
 * объясняет (нет прав, отчёт не найден), повторять бессмысленно — он уходит
 * человеку сразу.
 */
export async function waitForReport(id: string, signal: AbortSignal): Promise<Report> {
  const deadline = Date.now() + EXPORT_TIMEOUT_MS;
  let delay = EXPORT_POLL_MS;
  let misses = 0;
  for (;;) {
    await sleep(delay, signal);
    if (Date.now() > deadline) throw new ExportFailure(TOO_SLOW);
    let fresh: Report;
    try {
      const result = await requestWith<Report>(`/reports/${id}`, { signal });
      fresh = result.body;
      delay = (result.retryAfter ?? EXPORT_POLL_MS / 1000) * 1000;
      misses = 0;
    } catch (error) {
      if (signal.aborted) throw error;
      // Отказ, о котором сервер сказал внятно и который не пройдёт сам.
      if (
        error instanceof ApiError &&
        error.problem.status < 500 &&
        error.problem.status !== 429
      ) {
        throw error;
      }
      misses += 1;
      delay = Math.min(EXPORT_POLL_MS * 2 ** misses, EXPORT_POLL_MAX_MS);
      continue;
    }
    if (fresh.state === 'ready') return fresh;
    if (fresh.state === 'failed')
      throw new ExportFailure(fresh.last_error ?? BUILD_FAILED);
  }
}

/** Что именно выгружаем: область, период и вид отчёта. */
export interface ExportRequest {
  kind: ReportKind;
  format: ReportBuildFormat;
  brandId: string | null;
  workspaceId?: string | null;
  periodStart: string;
  periodEnd: string;
  /** Уже известные отчёты: среди них может лежать готовый файл нужного формата. */
  siblings?: Report[];
}

/**
 * Совпадает ли готовый отчёт с тем, что просят выгрузить.
 *
 * Пространство сверяется наравне с брендом. У отчёта по пространству `brand_id`
 * пуст, и без этой проверки отчёт того же вида за тот же период, но по ДРУГОМУ
 * клиенту, считался бы «уже собранным» — кнопка отдала бы агентству файл не
 * того клиента.
 */
function sameAsAsked(report: Report, ask: ExportRequest): boolean {
  return (
    // Отказавшая сборка не считается: её как раз и надо заказать заново.
    report.state !== 'failed' &&
    report.format === ask.format &&
    report.kind === ask.kind &&
    (report.brand_id ?? null) === ask.brandId &&
    (ask.workspaceId === undefined ||
      (report.workspace_id ?? null) === (ask.workspaceId ?? null)) &&
    report.period_start === ask.periodStart &&
    report.period_end === ask.periodEnd
  );
}

/**
 * Заказать файл и скачать его, когда он соберётся.
 *
 * Второй заказ того же файла не делается: у обычного отчёта его вернул бы
 * сервер, а у произвольного периода завёл бы вторую сборку — и человек получил
 * бы два одинаковых файла подряд.
 *
 * `onOrdered` вызывается один раз и сразу, чтобы список отчётов на экране
 * узнал о новой строке до того, как файл соберётся.
 */
export async function orderAndDownload(
  ask: ExportRequest,
  signal: AbortSignal,
  onOrdered?: (report: Report) => void,
): Promise<void> {
  const ready = (ask.siblings ?? [])
    .filter((one) => sameAsAsked(one, ask))
    .sort(
      (left, right) => Number(right.state === 'ready') - Number(left.state === 'ready'),
    );

  const ordered: Report =
    ready[0] ??
    (await api
      .post<Report>(
        '/reports',
        {
          kind: ask.kind,
          format: ask.format,
          brand_id: ask.brandId,
          ...(ask.brandId ? {} : { workspace_id: ask.workspaceId ?? null }),
          period_start: ask.periodStart,
          period_end: ask.periodEnd,
        },
        // Уход с экрана отменяет и сам заказ, а не только ожидание: иначе
        // закрытая вкладка оставляла бы за собой сборку, которую никто не
        // просил дособирать.
        { idempotencyKey: idempotencyKey(), signal },
      )
      .then((created) => {
        onOrdered?.(created);
        return created;
      }));

  const built =
    ordered.state === 'ready' ? ordered : await waitForReport(ordered.id, signal);
  if (signal.aborted) return;
  await downloadFile(`/reports/${built.id}/download`);
}
